import { useEffect, useRef, useState } from "react";
import AgoraRTC from "agora-rtc-sdk-ng";
import { isSafari, isIOSSafari, getMediaConstraints } from "../utils/safariHelpers";

/**
 * A custom React hook that:
 *  - Joins/Leaves an Agora channel
 *  - Publishes/Unpublishes local audio/video (if host)
 *  - Provides methods for playing a buffer-source audio track (music)
 *  - Tracks remote publishing users in `remoteUsers`
 *  - Tracks *all* joined user UIDs in `joinedUids` (including silent audience)
 *  - Exposes `totalUsers`, counting joinedUids + the local user
 */
export function useAgoraRTC({
  appId,
  channel,
  token,
  uid = null,         // optional numeric UID
  role = "host",      // "host" or "audience"
  enableAudio = false,
  enableVideo = false,
  onJoinSuccess,
}) {
  // Local track states
  const [localAudioTrack, setLocalAudioTrack] = useState(null);
  const [localVideoTrack, setLocalVideoTrack] = useState(null);
  const [localScreenTrack, setLocalScreenTrack] = useState(null);
  const [isScreenSharing, setIsScreenSharing] = useState(false);

  // Remote users who have published tracks
  const [remoteUsers, setRemoteUsers] = useState([]);

  // All user UIDs in the channel (including silent audience)
  const [joinedUids, setJoinedUids] = useState([]);

  // totalUsers = joinedUids + (1 if local user is present)
  const [totalUsers, setTotalUsers] = useState(0);

  // The Agora client reference
  const clientRef = useRef(null);

  // Keep track of a buffer-source track for music playback
  const [musicTrack, setMusicTrack] = useState(null);

  // ==========================================================
  // 1) Initialize the Agora client
  // ==========================================================
  useEffect(() => {
    if (!clientRef.current) {
      clientRef.current = AgoraRTC.createClient({ mode: "live", codec: "vp8" });
    }
  }, []);

  // ==========================================================
  // 1B) Recompute totalUsers whenever joinedUids changes
  // ==========================================================
  useEffect(() => {
    const client = clientRef.current;
    if (!client) {
      console.log("[useAgoraRTC] No Agora client => setTotalUsers(0)");
      setTotalUsers(0);
      return;
    }
    const localCount = client.uid != null ? 1 : 0;
    const newTotal = joinedUids.length + localCount;
    console.log("[useAgoraRTC] Recompute totalUsers =>", newTotal);
    setTotalUsers(newTotal);
  }, [joinedUids]);

  // ==========================================================
  // 2) Join an Agora channel
  // ==========================================================
  const joinChannel = async () => {
    console.log("[joinChannel] Called with params:", { appId, channel, token, uid, role, enableAudio, enableVideo });
    const client = clientRef.current;
    if (!client) {
      console.warn("[joinChannel] No Agora client => cannot join");
      return;
    }
    if (!channel) {
      console.warn("[joinChannel] No channel => cannot join");
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      console.error("[joinChannel] getUserMedia not supported => must use HTTPS or modern browser");
      alert("Your browser doesn't support getUserMedia. Please use HTTPS or a modern browser.");
      return;
    }
  
    // Check the connection state before attempting to join
    if (client.connectionState === "CONNECTED") {
      console.log("[joinChannel] Client already connected. Skipping join.");
      return;
    }
    if (client.connectionState === "CONNECTING") {
      console.log("[joinChannel] Client is already connecting. Skipping join.");
      return;
    }
  
    try {
      console.log(`[joinChannel] role=${role}, channel=${channel}, appId=${appId}`);
      await client.setClientRole(role);
  
      // Ensure token is valid (use null if no token is required)
      const validToken = token || null;
      console.log("[joinChannel] Using token:", validToken, " uid:", uid);
  
      // Actually join the channel
      await client.join(appId, channel, validToken, uid);
      const joinedUid = client.uid;
      console.log("[joinChannel] Joined => local UID:", joinedUid);
  
      if (onJoinSuccess) {
        onJoinSuccess(joinedUid);
      }
  
      // If role is host, publish local tracks
      if (role === "host") {
        if (enableAudio) {
          try {
            console.log("[joinChannel] Creating microphone audio track...");
            // Use Safari-compatible constraints
            const audioConstraints = isSafari() ? { 
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true 
            } : undefined;
            
            const micTrack = await AgoraRTC.createMicrophoneAudioTrack(audioConstraints);
            await client.publish(micTrack);
            console.log("[joinChannel] Mic track published => setLocalAudioTrack");
            setLocalAudioTrack(micTrack);
          } catch (err) {
            console.error("[joinChannel] Failed to create/publish mic:", err);
            // Safari-specific error handling
            if (isSafari() && err.message.includes("NotAllowedError")) {
              alert("Microphone access denied. Safari requires user interaction to access microphone. Please click allow when prompted.");
            }
          }
        }
      if (enableVideo) {
          try {
            // Check camera permissions first (wrapped in try-catch as it may not be supported)
            try {
              console.log("[joinChannel] Checking camera permissions...");
              const permissions = await navigator.permissions.query({ name: 'camera' });
              console.log("[joinChannel] Camera permission state:", permissions.state);
            } catch (permErr) {
              console.log("[joinChannel] Permissions API not supported, continuing anyway");
            }
            
            // Only create and publish a new video track if one doesn't already exist
            if (!localVideoTrack) {
              console.log("[joinChannel] Creating camera track...");
              
              // Use landscape dimensions for web streaming
              const videoConfig = {
                encoderConfig: "720p_2", // Agora's 720p landscape profile (1280x720)
                optimizationMode: "detail",
                // Explicitly set camera capture to landscape
                cameraVideoTrackConfig: {
                  // Force landscape capture from camera
                  width: { min: 1280, ideal: 1280, max: 1920 },
                  height: { min: 720, ideal: 720, max: 1080 },
                  frameRate: 30,
                  facingMode: "user"
                }
              };
              
              const camTrack = await AgoraRTC.createCameraVideoTrack(videoConfig);
              console.log("[joinChannel] Camera track created successfully");
              await client.publish(camTrack);
              console.log("[joinChannel] Camera track published => setLocalVideoTrack");
              setLocalVideoTrack(camTrack);
            } else {
              console.log("[joinChannel] Video track already exists, not creating a new one.");
            }
          } catch (err) {
            console.error("[joinChannel] Failed to create/publish cam:", err);
            console.error("[joinChannel] Error details:", err.message, err.code);
            // Try to show user-friendly error
            if (err.code === "PERMISSION_DENIED" || err.message.includes("Permission")) {
              alert("Camera permission denied. Please allow camera access and refresh the page.");
            } else if (err.code === "DEVICE_NOT_FOUND") {
              alert("No camera found. Please connect a camera device.");
            }
          }
        }
      }
  
      // Attach event handlers after joining
      client.on("user-joined", handleUserJoined);
      client.on("user-published", handleUserPublished);
      client.on("user-unpublished", handleUserUnpublished);
      client.on("user-left", handleUserLeft);
      
      // Add connection state monitoring
      client.on("connection-state-change", (curState, prevState, reason) => {
        console.log(`[Agora] Connection state changed: ${prevState} → ${curState}, reason: ${reason}`);
        if (curState === "DISCONNECTED" || curState === "FAILED") {
          console.error("[Agora] Connection failed/disconnected, may need to rejoin");
        }
      });
      
      client.on("network-quality", (stats) => {
        if (stats.downlinkNetworkQuality <= 2 || stats.uplinkNetworkQuality <= 2) {
          console.warn("[Agora] Poor network quality detected:", stats);
        }
      });
    } catch (error) {
      console.error("[joinChannel] Error joining channel:", error);
    }
  };

  // ==========================================================
  // 3) Leave the channel
  // ==========================================================
  const leaveChannel = async () => {
    const client = clientRef.current;
    if (!client) {
      console.warn("[leaveChannel] => no client => cannot leave");
      return;
    }
    console.log("[leaveChannel] Unpublishing & closing local tracks...");

    if (localAudioTrack) {
      try {
        console.log("[leaveChannel] => unpublish localAudioTrack");
        await client.unpublish(localAudioTrack);
      } catch (err) {
        console.error("[leaveChannel] Error unpublishing audio:", err);
      }
      localAudioTrack.close();
      setLocalAudioTrack(null);
    }

    if (localVideoTrack) {
      try {
        console.log("[leaveChannel] => unpublish localVideoTrack");
        await client.unpublish(localVideoTrack);
      } catch (err) {
        console.error("[leaveChannel] Error unpublishing video:", err);
      }
      localVideoTrack.close();
      setLocalVideoTrack(null);
    }

    if (musicTrack) {
      try {
        console.log("[leaveChannel] => stop/unpublish music track");
        musicTrack.stopProcessAudioBuffer();
        await client.unpublish(musicTrack);
        musicTrack.close();
        setMusicTrack(null);
      } catch (err) {
        console.error("[leaveChannel] Error unpublishing music track:", err);
      }
    }

    // Remove all event listeners
    client.off("user-joined", handleUserJoined);
    client.off("user-published", handleUserPublished);
    client.off("user-unpublished", handleUserUnpublished);
    client.off("user-left", handleUserLeft);
    client.off("connection-state-change");
    client.off("network-quality");

    setRemoteUsers([]);
    setJoinedUids([]);

    console.log("[leaveChannel] => leaving agora channel...");
    await client.leave();
    console.log("[leaveChannel] => left channel successfully.");
    setTotalUsers(0);
  };

  // ==========================================================
  // 4) Remote user event handlers
  // ==========================================================
  const handleUserJoined = (user) => {
    if (!user || !user.uid) {
      console.warn("[handleUserJoined] => Invalid user data:", user);
      return;
    }
    console.log(`[handleUserJoined] => remote uid=${user.uid}`);
    setRemoteUsers((prev) => {
      const alreadyIn = prev.some((entry) => entry.uid === user.uid);
      if (alreadyIn) return prev;
      return [...prev, user];
    });
  };

  const handleUserPublished = async (user, mediaType) => {
    const client = clientRef.current;
    try {
      await client.subscribe(user, mediaType);
      if (mediaType === "video" && user.videoTrack) {
        console.log(`[handleUserPublished] => playing remote video => #player-${user.uid}`);
        user.videoTrack.play(`player-${user.uid}`);
      }
      if (mediaType === "audio" && user.audioTrack) {
        console.log(`[handleUserPublished] => playing remote audio => uid=${user.uid}`);
        user.audioTrack.play();
      }
      setRemoteUsers([...client.remoteUsers]);
    } catch (error) {
      console.error(`[handleUserPublished] Error subscribing to user ${user.uid}:`, error);
    }
  };

  const handleUserUnpublished = (user, mediaType) => {
    console.log(`[handleUserUnpublished] => uid=${user.uid}, type=${mediaType}`);
    const client = clientRef.current;
    setRemoteUsers([...client.remoteUsers]);
  };

  const handleUserLeft = (user) => {
    console.log(`[handleUserLeft] => uid=${user.uid}`);
    const client = clientRef.current;
    setRemoteUsers([...client.remoteUsers]);
    setJoinedUids((prev) => prev.filter((id) => id !== user.uid));
  };

  // ==========================================================
  // 5) Local media helper methods
  // ==========================================================
  const muteAudio = async () => {
    if (!localAudioTrack) return;
    console.log("[muteAudio] => muting local mic");
    await localAudioTrack.setEnabled(false);
  };

  const unmuteAudio = async () => {
    if (!localAudioTrack) return;
    console.log("[unmuteAudio] => unmuting local mic");
    await localAudioTrack.setEnabled(true);
  };

  const enableLocalVideo = async () => {
    const client = clientRef.current;
    if (!localVideoTrack) {
      console.log("[enableLocalVideo] => creating camera track...");
      try {
        const camTrack = await AgoraRTC.createCameraVideoTrack();
        await client.publish(camTrack);
        console.log("[enableLocalVideo] => published camera track");
        setLocalVideoTrack(camTrack);
      } catch (err) {
        console.error("[enableLocalVideo] => error creating/publishing camera:", err);
        throw err;
      }
    } else {
      console.log("[enableLocalVideo] => enabling existing camera track");
      await localVideoTrack.setEnabled(true);
    }
  };

  const disableLocalVideo = async () => {
    if (!localVideoTrack) return;
    console.log("[disableLocalVideo] => disabling local camera track");
    await localVideoTrack.setEnabled(false);
  };

  // ==========================================================
  // 6) Music track publishing
  // ==========================================================
  const publishMusicTrack = async (fileBlob, volume = 100) => {
    const client = clientRef.current;
    if (!client) {
      console.warn("[publishMusicTrack] => No Agora client => cannot publish music");
      return undefined;
    }
    try {
      console.log("[publishMusicTrack] => creating buffer source track => volume=", volume);
      const track = await AgoraRTC.createBufferSourceAudioTrack({ source: fileBlob });
      track.startProcessAudioBuffer();
      track.play();
      track.setVolume(volume);
      await client.publish(track);
      setMusicTrack(track);

      track.on("state-changed", (curState, prevState) => {
        console.log(`[publishMusicTrack] => track state changed from ${prevState} to ${curState}`);
        if (curState === "stopped" && typeof track.onEnded === "function") {
          track.onEnded();
        }
      });

      console.log("[publishMusicTrack] => music track published");
      return track;
    } catch (err) {
      console.error("[publishMusicTrack] => failed to publish track:", err);
      return undefined;
    }
  };

  const stopMusicTrack = async () => {
    const client = clientRef.current;
    if (!client || !musicTrack) return;
    try {
      console.log("[stopMusicTrack] => stopping & unpublishing music track");
      musicTrack.stopProcessAudioBuffer();
      await client.unpublish(musicTrack);
      musicTrack.close();
      setMusicTrack(null);
      console.log("[stopMusicTrack] => music track fully stopped");
    } catch (err) {
      console.error("[stopMusicTrack] => failed to stop/unpublish music track:", err);
    }
  };

  // ==========================================================
  // 7) Dynamic Role Switching Helper
  // ==========================================================
  const setClientRole = async (newRole) => {
    const client = clientRef.current;
    if (client) {
      try {
        console.log(`[setClientRole] => switching role to ${newRole}`);
        await client.setClientRole(newRole);
      } catch (err) {
        console.error("[setClientRole] => error switching role:", err);
        throw err;
      }
    }
  };

  // ==========================================================
  // 8) Screen Sharing Functions
  // ==========================================================
  const startScreenShare = async () => {
    const client = clientRef.current;
    if (!client) {
      console.error("[startScreenShare] => no client available");
      return;
    }

    try {
      console.log("[startScreenShare] => creating screen track");
      
      // Create screen sharing track
      const screenTrack = await AgoraRTC.createScreenVideoTrack({
        encoderConfig: "1080p_1",
        optimizationMode: "detail",
      });

      console.log("[startScreenShare] => screen track created, publishing...");
      
      // Unpublish camera video if sharing
      if (localVideoTrack) {
        await client.unpublish(localVideoTrack);
        console.log("[startScreenShare] => unpublished camera video");
      }

      // Publish screen track
      await client.publish(screenTrack);
      
      // Update state
      setLocalScreenTrack(screenTrack);
      setIsScreenSharing(true);
      
      console.log("[startScreenShare] => screen sharing started successfully");

      // Handle screen share ending (user clicks browser's stop sharing)
      screenTrack.on("track-ended", () => {
        console.log("[startScreenShare] => screen sharing ended by user");
        stopScreenShare();
      });

    } catch (error) {
      console.error("[startScreenShare] => error starting screen share:", error);
      setIsScreenSharing(false);
      throw error;
    }
  };

  const stopScreenShare = async () => {
    const client = clientRef.current;
    if (!client || !localScreenTrack) {
      console.log("[stopScreenShare] => no client or screen track to stop");
      return;
    }

    try {
      console.log("[stopScreenShare] => stopping screen share");
      
      // Unpublish and close screen track
      await client.unpublish(localScreenTrack);
      localScreenTrack.close();
      
      // Update state
      setLocalScreenTrack(null);
      setIsScreenSharing(false);
      
      // Re-publish camera video if available
      if (localVideoTrack) {
        await client.publish(localVideoTrack);
        console.log("[stopScreenShare] => re-published camera video");
      }
      
      console.log("[stopScreenShare] => screen sharing stopped successfully");
      
    } catch (error) {
      console.error("[stopScreenShare] => error stopping screen share:", error);
      setIsScreenSharing(false);
      throw error;
    }
  };

  // ==========================================================
  // Return everything your parent component needs
  // ==========================================================
  return {
    localAudioTrack,
    localVideoTrack,
    localScreenTrack,
    isScreenSharing,
    setLocalVideoTrack,
    remoteUsers,
    joinedUids,
    totalUsers,
    client: clientRef.current,
    joinChannel,
    leaveChannel,
    muteAudio,
    unmuteAudio,
    enableLocalVideo,
    disableLocalVideo,
    publishMusicTrack,
    stopMusicTrack,
    setClientRole,
    startScreenShare,
    stopScreenShare,
  };
}
